import { Dialog, DialogTitle, DialogContent, TextField, Button, DialogActions, Typography } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Save, WrapText, CancelRounded, ChevronLeft, ChevronRight } from "@material-ui/icons";
import _ from "lodash-core";
import { IconButton } from "@material-ui/core";

const SEPARATOR_OPTIONS = ["|", ",", "-", "#", ":", ";", "*", "_", "+"];
const SEPARATOR_STORAGE_PREFIX = "dialogTextEdit.separatorIndex";

/**
 * Dark chrome. variant="dark" repaints this dialog in the inspector palette so
 * it does not drop the light Material theme on top of a dark editor. Values
 * match the scene editor and the Design inspector. "sceneEditor" is the name
 * the variant was introduced under and stays accepted.
 */
const SCENE = {
  panel: "#2b2b2e",
  divider: "#3a3a3f",
  input: "#212226",
  bar: "#3a3a41",
  text: "#e8e8e8",
  label: "#b8bcc4",
  muted: "#8a8d93",
};

const SCENE_BUTTON = {
  color: SCENE.text,
  fontSize: 11,
  textTransform: "none",
  minWidth: 0,
  padding: "4px 14px",
  backgroundColor: SCENE.input,
  border: `1px solid ${SCENE.divider}`,
  borderRadius: 4,
  lineHeight: 1.4,
  boxShadow: "none",
};

function getStorageKey(fieldKey) {
  return fieldKey ? `${SEPARATOR_STORAGE_PREFIX}.${fieldKey}` : SEPARATOR_STORAGE_PREFIX;
}

function readStoredSeparatorIndex(fieldKey) {
  try {
    const raw = window.localStorage.getItem(getStorageKey(fieldKey));
    const parsed = parseInt(raw, 10);
    if (Number.isInteger(parsed) && parsed >= 0 && parsed < SEPARATOR_OPTIONS.length) {
      return parsed;
    }
  } catch (e) {
    // localStorage unavailable — fall through to default
  }
  return 0;
}

// Some string fields (e.g. Level Manager's levelJson1..4) hold a JSON document
// as a flat string. Detect that shape so the dialog can pretty-print, validate
// and re-minify it instead of applying separator line breaks that corrupt it.
function looksLikeJson(text) {
  const trimmed = (typeof text === "string" ? text : "").trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function jsonErrorOf(text) {
  try {
    JSON.parse(text);
    return "";
  } catch (e) {
    return e.message;
  }
}

function prettyJson(text) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (e) {
    return text;
  }
}

function arrangeForDisplay(input, sep) {
  const text = typeof input === "string" ? input : "";
  const flat = text.replaceAll("\n", "");
  if (sep && flat.includes(sep)) {
    return flat.replaceAll(sep, `${sep}\n`);
  }
  return flat;
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    overflow: "auto",
    margin: "1rem 1rem 1rem 1rem",
  },
  containerDialog: {
    width: "30rem",
  },
  containerDialogWide: {
    width: "46rem",
  },
  jsonError: {
    color: "#f48fb1",
    fontFamily: "monospace",
    fontSize: "0.8rem",
    padding: "0.5rem 1rem",
    wordBreak: "break-word",
  },
  textCenter: {
    textAlign: "left",
  },
  textField: {
    width: "150%",
    fontFamily: "monospace",
    "& textarea": {
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(255,255,255,0.25) transparent",
    },
    "& textarea::-webkit-scrollbar": {
      width: "8px",
    },
    "& textarea::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.25)",
      borderRadius: "4px",
    },
    "& textarea::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
  dialog: {
    marginTop: "4rem",
    display: "flex",
    justifyContent: "flex-end",
  },
  saveButton: {
    width: "30%",
  },
  cancelButton: {
    padding: "0rem 1rem 0rem 1rem",
  },
  arrangeButton: {
    width: "30%",
  },
  dialogActions: {
    padding: "1.8rem",
    justifyContent: "space-between",
  },
  titleWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  separatorWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "0.5rem",
    padding: "0.5rem 1rem",
  },
  separatorLabel: {
    marginRight: "0.5rem",
  },
  separatorValue: {
    fontFamily: "monospace",
    fontSize: "1.25rem",
    minWidth: "1.5rem",
    textAlign: "center",
    padding: "0.25rem 0.5rem",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "4px",
  },
  separatorArrow: {
    padding: "0.25rem",
  },
}));

export default function dialogTextEdit(props) {
  const fieldKey = _.get(props, "storageKey", "");
  const rawValue = _.get(props, "value", "");
  // Some fields (e.g. Word Manager level configs) hold an object, not a flat string.
  // Show it as editable JSON and parse it back on save so it round-trips intact.
  const isObjectValue = rawValue !== null && typeof rawValue === "object";
  const [separatorIndex, setSeparatorIndex] = useState(() => readStoredSeparatorIndex(fieldKey));
  const separator = SEPARATOR_OPTIONS[separatorIndex];
  const [value, setValue] = useState(() => {
    if (isObjectValue) return JSON.stringify(rawValue, null, 2);
    if (looksLikeJson(rawValue)) return prettyJson(rawValue);
    return arrangeForDisplay(rawValue || "", SEPARATOR_OPTIONS[readStoredSeparatorIndex(fieldKey)]);
  });
  const [isOpen, setIsOpen] = useState(_.get(props, `open`, ""));
  const classes = useStyles();
  const isSceneEditor = ["dark", "sceneEditor"].includes(_.get(props, "variant"));
  const textareaRef = useRef(null);
  // A string field holding JSON is edited as JSON: pretty-printed here, stored
  // back as a minified single-line string, which is what the engine reads.
  const isJsonText = !isObjectValue && looksLikeJson(value);
  const jsonError = isJsonText ? jsonErrorOf(value) : "";
  // The dark variant's six lines and narrow box were sized for the scene
  // editor's inspector. A caller with more room on screen asks for more.
  const rowsMax = _.get(props, "rowsMax", isSceneEditor ? 6 : 25);
  const panelWidth =
    _.get(props, "width", null) || (isSceneEditor ? (isJsonText ? "40rem" : "26rem") : null);

  // A long document opens at its beginning, not at whatever the textarea
  // scrolled to while it was being laid out.
  useEffect(() => {
    if (textareaRef.current) textareaRef.current.scrollTop = 0;
  }, []);

  function cycleSeparator(direction) {
    const length = SEPARATOR_OPTIONS.length;
    setSeparatorIndex((prev) => (prev + direction + length) % length);
  }

  function previewStored(stored) {
    if (typeof props.livePreview === "function") {
      props.livePreview(stored);
    }
  }

  function storedFromEditorText(text) {
    if (isObjectValue) {
      try {
        return JSON.parse(text);
      } catch (e) {
        return undefined;
      }
    }
    if (looksLikeJson(text)) {
      try {
        return JSON.stringify(JSON.parse(text));
      } catch (e) {
        return undefined;
      }
    }
    return (typeof text === "string" ? text : "").replaceAll("\n", "");
  }

  function handleCloseCancel() {
    previewStored(isObjectValue ? rawValue : storedFromEditorText(rawValue));
    setIsOpen(false);
    props.onClose();
  }

  function handleCloseSave() {
    if (isObjectValue) {
      let parsed;
      try {
        parsed = JSON.parse(value);
      } catch (e) {
        // Invalid JSON — keep the dialog open so edits are not lost and can be fixed.
        return;
      }
      props.setValue(parsed);
      setIsOpen(false);
      props.onClose();
      return;
    }
    if (isJsonText) {
      if (jsonError) return; // keep the dialog open so the edit is not lost
      props.setValue(JSON.stringify(JSON.parse(value)));
      setIsOpen(false);
      props.onClose();
      return;
    }
    props.setValue(value.replaceAll("\n", ""));
    try {
      window.localStorage.setItem(getStorageKey(fieldKey), String(separatorIndex));
    } catch (e) {
      // localStorage unavailable — separator preference not persisted this time
    }
    setIsOpen(false);
    props.onClose();
  }

  function arrangeMapString() {
    if (isObjectValue) return; // separator formatting would corrupt JSON
    if (isJsonText) {
      setValue((prev) => prettyJson(prev));
      return;
    }
    setValue((prev) => {
      const next = arrangeForDisplay(prev, separator);
      return prev && !next ? prev : next;
    });
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.scrollTop = 0;
      }
    });
  }

  return (
    <div className={classes.wrapper}>
      <Dialog
        open={isOpen}
        onClose={handleCloseCancel}
        className={classes.dialog}
        PaperProps={
          isSceneEditor
            ? {
              style: {
                backgroundColor: SCENE.panel,
                color: SCENE.text,
                border: `1px solid ${SCENE.divider}`,
                borderRadius: 6,
              },
            }
            : undefined
        }
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <div className={classes.titleWrapper}>
          <DialogTitle
            id="mapEditTitle"
            className={classes.textCenter}
            disableTypography={isSceneEditor}
            style={isSceneEditor ? { padding: "12px 16px 8px" } : undefined}
          >
            {isSceneEditor ? (
              <span style={{ fontSize: 13, fontWeight: 600, color: SCENE.text }}>
                {props.label || "Edit Map"}
              </span>
            ) : (
              "Edit Map"
            )}
          </DialogTitle>
          <IconButton
            color={isSceneEditor ? "default" : "secondary"}
            className={classes.cancelButton}
            component="span"
            onClick={handleCloseCancel}
            style={isSceneEditor ? { color: SCENE.label } : undefined}
          >
            <CancelRounded />
          </IconButton>
        </div>
        <DialogContent
          dividers={true}
          style={isSceneEditor ? { borderColor: SCENE.divider } : undefined}
        >
          <div
            className={isJsonText ? classes.containerDialogWide : classes.containerDialog}
            style={panelWidth ? { width: panelWidth } : undefined}
          >
            <div className={classes.textCenter}>
              <TextField
                multiline
                rows={2}
                rowsMax={rowsMax}
                className={classes.textField}
                style={{ width: isSceneEditor ? "100%" : "calc(100% - 5px)" }}
                label={isSceneEditor ? undefined : props.label}
                value={value}
                onChange={(event) => {
                  const next = event.target.value;
                  setValue(next);
                  const stored = storedFromEditorText(next);
                  if (typeof stored !== "undefined") {
                    previewStored(stored);
                  }
                }}
                inputRef={textareaRef}
                InputProps={{
                  className: classes.textField,
                  disableUnderline: true,
                  style: isSceneEditor
                    ? {
                      color: SCENE.text,
                      fontSize: 12,
                      fontFamily: "monospace",
                      backgroundColor: SCENE.input,
                      border: `1px solid ${SCENE.divider}`,
                      borderRadius: 4,
                      padding: "8px 10px",
                      // classes.textField sets width:150% for the classic panel —
                      // on the input that spilled past the dialog's right edge.
                      width: "100%",
                      boxSizing: "border-box",
                    }
                    : undefined,
                }}
              ></TextField>
            </div>
          </div>
        </DialogContent>
        {jsonError ? <div className={classes.jsonError}>Invalid JSON — {jsonError}</div> : null}
        {isJsonText ? null : (
        <div className={classes.separatorWrapper}>
          <Typography
            className={classes.separatorLabel}
            style={isSceneEditor ? { fontSize: 12, color: SCENE.label } : undefined}
          >
            Line break separator:
          </Typography>
          <IconButton
            className={classes.separatorArrow}
            color={isSceneEditor ? "default" : "secondary"}
            size="small"
            onClick={() => cycleSeparator(-1)}
            aria-label="previous separator"
            style={isSceneEditor ? { color: SCENE.label } : undefined}
          >
            <ChevronLeft />
          </IconButton>
          <span
            className={classes.separatorValue}
            style={
              isSceneEditor
                ? {
                  fontSize: 14,
                  color: SCENE.text,
                  backgroundColor: SCENE.bar,
                  border: `1px solid ${SCENE.divider}`,
                }
                : undefined
            }
          >
            {separator}
          </span>
          <IconButton
            className={classes.separatorArrow}
            color={isSceneEditor ? "default" : "secondary"}
            size="small"
            onClick={() => cycleSeparator(1)}
            aria-label="next separator"
            style={isSceneEditor ? { color: SCENE.label } : undefined}
          >
            <ChevronRight />
          </IconButton>
        </div>
        )}
        <DialogActions
          className={classes.dialogActions}
          style={isSceneEditor ? { padding: "10px 16px 14px" } : undefined}
        >
          <Button
            color={isSceneEditor ? "default" : "secondary"}
            className={classes.arrangeButton}
            variant="contained"
            startIcon={<WrapText style={isSceneEditor ? { fontSize: 15 } : undefined} />}
            onClick={arrangeMapString}
            style={isSceneEditor ? SCENE_BUTTON : undefined}
          >
            {isJsonText ? "Format" : "Arrange"}
          </Button>
          <Button
            color={isSceneEditor ? "default" : "secondary"}
            className={classes.saveButton}
            variant="contained"
            disabled={Boolean(jsonError)}
            startIcon={<Save style={isSceneEditor ? { fontSize: 15 } : undefined} />}
            onClick={handleCloseSave}
            style={
              isSceneEditor
                ? { ...SCENE_BUTTON, color: jsonError ? SCENE.muted : SCENE.text }
                : undefined
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
